import { useMergeRefs } from "@floating-ui/react"
import type {
  ChangeEventHandler,
  FocusEventHandler,
  KeyboardEventHandler,
  HTMLProps,
  Ref,
} from "react"
import {
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react"
import { useValidationContext } from "./ValidationContext"
import type { ValidationElement } from "./ValidationElement"

export function useValidationElementProps<
  T extends ValidationElement,
  Props extends HTMLProps<T>,
>(props: Props, propRef?: Ref<T>): Props & { ref: Ref<T> } {
  const validationContext = useValidationContext()

  const ref = useMergeRefs([
    propRef,
    validationContext?.validationElementProps.ref,
    validationContext?.validationElementProps.refCallback,
  ])

  const onBlur: FocusEventHandler<T> = useCallback(
    (e) => {
      props.onBlur?.(e)
      validationContext?.validationElementProps.onBlur(e)
    },
    [
      props.onBlur,
      validationContext?.validationElementProps.onBlur,
    ],
  )

  const valueRef = useRef(props.value)

  // when the control value changes
  useEffect(
    () => {
      if (
        props.value !== undefined &&
        (props.value !== valueRef.current)
      ) {
        validationContext?.validationElementProps.onControlChange()
        valueRef.current = props.value
      }
    },
    [
      valueRef,
      props.value,
      validationContext?.validationElementProps.onChange,
    ],
  )

  const onChange: ChangeEventHandler<T> = useCallback(
    (e) => {
      props.onChange?.(e)

      if (props.value === undefined) {
        validationContext?.validationElementProps.onChange(e)
      }
    },
    [
      props.value,
      props.onChange,
      validationContext?.validationElementProps.onChange,
    ],
  )

  const onKeyDown: KeyboardEventHandler<T> = useCallback(
    (e) => {
      props.onKeyDown?.(e)
      validationContext?.validationElementProps.onKeyDown(e)
    },
    [
      props.onKeyDown,
      validationContext?.validationElementProps.onKeyDown,
    ],
  )

  return useMemo(
    () => ({
      ...props,
      ref,
      onBlur,
      onChange,
      onKeyDown,
    }),
    [
      ref,
      onBlur,
      onChange,
      onKeyDown,
      props,
    ],
  )
}
